import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { Card, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useFormikContext } from "formik";
import React, { useMemo } from "react";

import MDBadge from "@/components/atoms/MDBadge";
import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import MDTypography from "@/components/atoms/MDTypography";
import {
  HeaderTableCell,
  TableContainer,
  TableHeaderRow,
} from "@/components/molecules/Table/styled";
import { OrderFormData } from "@/components/screens/OrderFormPage/types";
import { formatCurrency } from "@/helpers/currency-helpers";
import useDialog from "@/hooks/use-dialog";
import { ItemDataTabMessages } from "@/messages/order-form-page/item-data-tab.messages";

import { ItemTableRow } from "./row";
import { ITEMS_TABLE_COLUMNS } from "../../constants";
import { getOrderItemTotals } from "../../form-data";
import NewProductDialog, { NewProductPayload } from "../NewProductDialog";

interface Props {
  messages: ItemDataTabMessages;
  handleAddProduct: (payload: NewProductPayload) => void;
}

export function ItemTable({ messages, handleAddProduct }: Props) {
  const { values } = useFormikContext<OrderFormData>();
  const { items, disabledButton } = values;

  const columns = ITEMS_TABLE_COLUMNS(messages);

  const { isVisible, handleOpen, handleClose } = useDialog();

  const existingSkuCodes = useMemo(() => items.map((item) => String(item.skuCode)), [items]);

  const orderTotal = useMemo(() => getOrderItemTotals(items).netTotal, [items]);

  return (
    <Card>
      <MDBox p={3}>
        <MDBox display="flex" justifyContent="space-between" alignItems="center" gap={2}>
          <MDTypography variant="h5" fontWeight={"bold"}>
            {messages.itemInformationTitle}
          </MDTypography>

          {!disabledButton && (
            <MDButton
              variant="gradient"
              color="dark"
              onClick={() => handleOpen()}
              endIcon={<AddCircleOutlineIcon />}
            >
              {messages.newProductButton}
            </MDButton>
          )}
        </MDBox>

        <MDBox mt={1}>
          <Grid container spacing={3}>
            <TableContainer sx={{ marginTop: 1 }}>
              <Table stickyHeader aria-label="sticky table">
                <TableHead>
                  <TableHeaderRow>
                    {columns.map((label, index) => {
                      const isNumeric = index >= 3 && index <= 5;
                      return (
                        <HeaderTableCell
                          key={`${index} - ${label}`}
                          align={isNumeric ? "right" : "left"}
                          sx={isNumeric ? { width: "1%", whiteSpace: "nowrap" } : undefined}
                        >
                          {label}
                        </HeaderTableCell>
                      );
                    })}

                  </TableHeaderRow>
                </TableHead>

                <TableBody>
                  {items && items.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={columns.length} align="center">
                        <MDTypography variant="h6" color="text" fontWeight="regular">
                          {messages.noItemsMessage}
                        </MDTypography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    items.map((singleItem, index) => (
                      <ItemTableRow
                        key={`${singleItem.skuCode || singleItem.id || index}`}
                        item={singleItem}
                        index={index}
                        messages={messages}
                      />
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </MDBox>

        <MDBox mt={2} display="flex" justifyContent="flex-end">
          <MDBadge
            color="light"
            size="md"
            badgeContent={`${messages.orderAmountLabel}: ${formatCurrency(orderTotal || 0)}`}
            container
          />
        </MDBox>
      </MDBox>

      <NewProductDialog
        open={isVisible}
        messages={messages}
        existingSkuCodes={existingSkuCodes}
        campaignId={values.campaignId ?? null}
        contactId={values.contactId ?? null}
        initialCouponCode={values.discountCouponCode || ""}
        onClose={handleClose}
        onConfirm={handleAddProduct}
      />
    </Card>
  );
}
